import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Bike, 
  CheckCircle2, 
  Sparkles, 
  ChevronRight,
  Zap,
  Mountain,
  Baby,
  Timer,
  MapPin,
  Star,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bike type configurations
const BIKE_TYPES = [
  { 
    id: 'road_bike', 
    name: 'Road Bike', 
    icon: '🚴',
    description: 'Racing & long-distance',
    color: 'cyan',
    features: ['Lightweight frame', 'Drop handlebars', 'Thin tires'],
  },
  { 
    id: 'mountain_bike', 
    name: 'Mountain Bike', 
    icon: '🚵',
    description: 'Off-road & trails',
    color: 'emerald',
    features: ['Suspension', 'Wide tires', 'Sturdy frame'],
  },
  { 
    id: 'ebike', 
    name: 'E-Bike', 
    icon: '⚡',
    description: 'Electric assisted',
    color: 'yellow',
    features: ['Battery system', 'Motor', 'Controller'],
  },
  { 
    id: 'kids_bike', 
    name: 'Kids Bike', 
    icon: '👶',
    description: 'For children',
    color: 'pink',
    features: ['Training wheels option', 'Safety features', 'Size appropriate'],
  },
  { 
    id: 'hybrid_bike', 
    name: 'Hybrid / Commuter', 
    icon: '🚲',
    description: 'City & casual',
    color: 'blue',
    features: ['Comfortable position', 'Medium tires', 'Versatile'],
  },
  { 
    id: 'bmx', 
    name: 'BMX', 
    icon: '🛹',
    description: 'Tricks & stunts',
    color: 'orange',
    features: ['Small frame', 'Strong wheels', 'Pegs'],
  },
  { 
    id: 'folding_bike', 
    name: 'Folding Bike', 
    icon: '📦',
    description: 'Portable & compact',
    color: 'purple',
    features: ['Foldable frame', 'Small wheels', 'Lightweight'],
  },
];

// Popular bike brands
const BIKE_BRANDS = [
  'Giant', 'Trek', 'Specialized', 'Cannondale', 'Scott', 
  'Merida', 'Cube', 'BMC', 'Bianchi', 'Canyon',
  'Santa Cruz', 'Cervélo', 'Pinarello', 'Brompton',
  'VanMoof', 'Rad Power', 'Other'
];

// Bike condition options
const BIKE_CONDITIONS = [
  { id: 'new_boxed', name: 'New (Still boxed)', icon: '📦' },
  { id: 'new_assembled', name: 'New (Already assembled)', icon: '✨' },
  { id: 'used_good', name: 'Used (Good condition)', icon: '👍' },
  { id: 'used_needs_work', name: 'Used (Needs work)', icon: '🔧' },
];

/**
 * Bike Compatibility Checker Component
 * Allows users to input their bike details and get personalized service recommendations
 */
const BikeCompatibilityChecker = ({ 
  onServiceSelect, 
  onBundleSelect,
  country = 'SL',
  currency = 'Rs',
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Select type, 2: Details, 3: Results
  const [bikeDetails, setBikeDetails] = useState({
    type: '',
    brand: '',
    model: '',
    year: '',
    condition: '',
    hasIssues: [],
  });
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);

  // Common bike issues
  const COMMON_ISSUES = [
    { id: 'brakes', name: 'Brake issues', icon: '🛑' },
    { id: 'gears', name: 'Gear shifting problems', icon: '⚙️' },
    { id: 'wheels', name: 'Wheel wobble/truing', icon: '🔄' },
    { id: 'comfort', name: 'Comfort/fit issues', icon: '🪑' },
    { id: 'suspension', name: 'Suspension problems', icon: '🔩' },
    { id: 'battery', name: 'Battery/motor issues', icon: '🔋' },
  ];

  const handleTypeSelect = (typeId) => {
    setBikeDetails(prev => ({ ...prev, type: typeId }));
    setStep(2);
  };

  const handleGetRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/sports-services/bike-compatibility-check`, {
        ...bikeDetails,
        country,
      });
      setRecommendations(response.data);
      setStep(3);
    } catch (error) {
      console.error('Error getting recommendations:', error);
      // Use fallback local recommendations
      setRecommendations(getLocalRecommendations(bikeDetails));
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  // Local fallback recommendations based on bike type
  const getLocalRecommendations = (details) => {
    const bikeType = BIKE_TYPES.find(t => t.id === details.type);
    
    const baseRecommendations = {
      road_bike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 3500 : 40 },
          { type: 'bike_fit', name: 'Bike Fit & Comfort Setup', priority: 'high', price: country === 'SL' ? 7000 : 80 },
          { type: 'safety_tuneup', name: 'Safety Check & Tune-Up', priority: 'medium', price: country === 'SL' ? 2000 : 25 },
        ],
        recommended_bundle: {
          id: 'comfort_performance',
          name: 'Comfort & Performance Pack',
          discount_percent: 12,
          original_price: country === 'SL' ? 12500 : 145,
          final_price: country === 'SL' ? 11000 : 128,
          included_services: ['bike_assembly', 'bike_fit', 'safety_tuneup'],
        },
        tips: [
          'Road bikes benefit greatly from a professional fit',
          'Regular tune-ups every 500-1000km recommended',
          'Consider a fit session if you experience any discomfort',
        ],
      },
      mountain_bike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 3500 : 40 },
          { type: 'suspension_setup', name: 'Suspension Setup', priority: 'high', price: country === 'SL' ? 5000 : 55 },
          { type: 'safety_tuneup', name: 'Safety Check & Tune-Up', priority: 'medium', price: country === 'SL' ? 2000 : 25 },
        ],
        recommended_bundle: {
          id: 'mtb_essentials',
          name: 'MTB Essentials Bundle',
          discount_percent: 15,
          original_price: country === 'SL' ? 10500 : 120,
          final_price: country === 'SL' ? 8925 : 102,
          included_services: ['bike_assembly', 'suspension_setup', 'safety_tuneup'],
        },
        tips: [
          'Proper suspension setup is crucial for performance and safety',
          'Check tire pressure before every ride',
          'Consider a tune-up after muddy/wet rides',
        ],
      },
      ebike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 4500 : 50 },
          { type: 'safety_tuneup', name: 'Safety Check & Tune-Up', priority: 'high', price: country === 'SL' ? 2500 : 30 },
          { type: 'ebike_diagnostics', name: 'E-Bike Diagnostics', priority: 'medium', price: country === 'SL' ? 4500 : 50 },
        ],
        recommended_bundle: {
          id: 'ebike_complete',
          name: 'E-Bike Complete Setup',
          discount_percent: 10,
          original_price: country === 'SL' ? 11500 : 130,
          final_price: country === 'SL' ? 10350 : 117,
          included_services: ['bike_assembly', 'safety_tuneup', 'ebike_diagnostics'],
        },
        tips: [
          'E-bikes require specialized knowledge for motor/battery',
          'Keep battery charged between 20-80% for longevity',
          'Annual diagnostics recommended for optimal performance',
        ],
      },
      kids_bike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 2500 : 30 },
          { type: 'safety_tuneup', name: 'Safety Check', priority: 'high', price: country === 'SL' ? 1500 : 20 },
        ],
        recommended_bundle: {
          id: 'kids_bike_bundle',
          name: "Kids' Bike Setup",
          discount_percent: 15,
          original_price: country === 'SL' ? 4000 : 50,
          final_price: country === 'SL' ? 3400 : 43,
          included_services: ['bike_assembly', 'safety_tuneup'],
        },
        tips: [
          'Safety is paramount - always get a professional check',
          'Ensure correct saddle height for your child',
          'Consider training wheels for beginners',
        ],
      },
      hybrid_bike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 3500 : 40 },
          { type: 'safety_tuneup', name: 'Safety Check & Tune-Up', priority: 'medium', price: country === 'SL' ? 2000 : 25 },
          { type: 'bike_fit', name: 'Comfort Setup', priority: 'low', price: country === 'SL' ? 5000 : 60 },
        ],
        recommended_bundle: {
          id: 'new_bike_essentials',
          name: 'New Bike Essentials',
          discount_percent: 15,
          original_price: country === 'SL' ? 5500 : 65,
          final_price: country === 'SL' ? 4675 : 55,
          included_services: ['bike_assembly', 'safety_tuneup'],
        },
        tips: [
          'Hybrid bikes are great for commuting and casual rides',
          'Regular tune-ups keep your bike running smoothly',
          'Consider mudguards and lights for commuting',
        ],
      },
      bmx: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 2500 : 30 },
          { type: 'safety_tuneup', name: 'Safety Check', priority: 'high', price: country === 'SL' ? 2000 : 25 },
        ],
        recommended_bundle: {
          id: 'bmx_setup',
          name: 'BMX Setup Bundle',
          discount_percent: 10,
          original_price: country === 'SL' ? 4500 : 55,
          final_price: country === 'SL' ? 4050 : 50,
          included_services: ['bike_assembly', 'safety_tuneup'],
        },
        tips: [
          'BMX bikes take a lot of stress - regular checks essential',
          'Ensure all bolts are properly tightened',
          'Consider protective gear for stunts',
        ],
      },
      folding_bike: {
        primary_services: [
          { type: 'bike_assembly', name: 'Bike Assembly', priority: 'high', price: country === 'SL' ? 3000 : 35 },
          { type: 'safety_tuneup', name: 'Safety Check & Tune-Up', priority: 'medium', price: country === 'SL' ? 2000 : 25 },
        ],
        recommended_bundle: {
          id: 'folding_setup',
          name: 'Folding Bike Setup',
          discount_percent: 10,
          original_price: country === 'SL' ? 5000 : 60,
          final_price: country === 'SL' ? 4500 : 54,
          included_services: ['bike_assembly', 'safety_tuneup'],
        },
        tips: [
          'Ensure folding mechanism works smoothly',
          'Check hinges and latches regularly',
          'Proper assembly is crucial for safety',
        ],
      },
    };

    const typeRecs = baseRecommendations[details.type] || baseRecommendations.hybrid_bike;
    
    // Modify based on condition
    if (details.condition === 'new_boxed') {
      typeRecs.primary_services[0].priority = 'critical';
    } else if (details.condition === 'used_needs_work') {
      typeRecs.primary_services.forEach(s => {
        if (s.type === 'safety_tuneup') s.priority = 'critical';
      });
    }

    // Add mobile repair if issues selected
    if (details.hasIssues?.length > 0) {
      typeRecs.primary_services.push({
        type: 'mobile_repair',
        name: 'Mobile Bike Repair',
        priority: 'high',
        price: country === 'SL' ? 3000 : 35,
        reason: `Address: ${details.hasIssues.join(', ')}`,
      });
    }

    return {
      bike_type: bikeType,
      ...typeRecs,
      match_score: 95,
    };
  };

  const handleReset = () => {
    setStep(1);
    setBikeDetails({
      type: '',
      brand: '',
      model: '',
      year: '',
      condition: '',
      hasIssues: [],
    });
    setRecommendations(null);
  };

  const toggleIssue = (issueId) => {
    setBikeDetails(prev => ({
      ...prev,
      hasIssues: prev.hasIssues.includes(issueId)
        ? prev.hasIssues.filter(i => i !== issueId)
        : [...prev.hasIssues, issueId],
    }));
  };

  return (
    <>
      {/* Trigger - Compact or Inline Card */}
      {compact ? (
        <Button 
          variant="outline" 
          className="gap-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
          onClick={() => setIsOpen(true)}
          data-testid="bike-compatibility-checker-btn"
        >
          <Sparkles className="h-4 w-4" />
          Find services for your bike
        </Button>
      ) : (
        <Card 
          className="border-2 border-dashed border-cyan-300 bg-gradient-to-r from-cyan-50 to-white hover:border-cyan-400 cursor-pointer transition-all"
          onClick={() => setIsOpen(true)}
          data-testid="bike-compatibility-checker-card"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="bg-cyan-100 p-3 rounded-full">
              <Bike className="h-6 w-6 text-cyan-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-600" />
                Bike Compatibility Checker
              </h4>
              <p className="text-sm text-gray-500">
                Get personalized service recommendations for your bike
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </CardContent>
        </Card>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bike className="h-5 w-5 text-cyan-600" />
              Bike Compatibility Checker
              {step > 1 && (
                <Badge variant="outline" className="ml-2">
                  Step {step} of 3
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Select Bike Type */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select your bike type to get personalized service recommendations
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {BIKE_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleTypeSelect(type.id)}
                    className={`p-4 border-2 rounded-lg text-left hover:border-${type.color}-400 hover:bg-${type.color}-50 transition-all group`}
                    data-testid={`bike-type-${type.id}`}
                  >
                    <span className="text-2xl">{type.icon}</span>
                    <h4 className="font-medium mt-2">{type.name}</h4>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Bike Details */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-2xl">
                  {BIKE_TYPES.find(t => t.id === bikeDetails.type)?.icon}
                </span>
                <div>
                  <p className="font-medium">
                    {BIKE_TYPES.find(t => t.id === bikeDetails.type)?.name}
                  </p>
                  <button 
                    onClick={() => setStep(1)}
                    className="text-xs text-cyan-600 hover:underline"
                  >
                    Change type
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand</Label>
                  <Select 
                    value={bikeDetails.brand}
                    onValueChange={(v) => setBikeDetails(prev => ({ ...prev, brand: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {BIKE_BRANDS.map(brand => (
                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Model (optional)</Label>
                  <Input 
                    placeholder="e.g., Domane SL5"
                    value={bikeDetails.model}
                    onChange={(e) => setBikeDetails(prev => ({ ...prev, model: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label>Condition</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {BIKE_CONDITIONS.map((condition) => (
                    <button
                      key={condition.id}
                      onClick={() => setBikeDetails(prev => ({ ...prev, condition: condition.id }))}
                      className={`p-3 border rounded-lg text-left transition-all ${
                        bikeDetails.condition === condition.id
                          ? 'border-cyan-400 bg-cyan-50'
                          : 'border-gray-200 hover:border-cyan-300'
                      }`}
                    >
                      <span className="mr-2">{condition.icon}</span>
                      <span className="text-sm">{condition.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Any current issues? (optional)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COMMON_ISSUES.filter(issue => {
                    // Filter issues based on bike type
                    if (issue.id === 'suspension' && !['mountain_bike'].includes(bikeDetails.type)) return false;
                    if (issue.id === 'battery' && bikeDetails.type !== 'ebike') return false;
                    return true;
                  }).map((issue) => (
                    <button
                      key={issue.id}
                      onClick={() => toggleIssue(issue.id)}
                      className={`px-3 py-2 border rounded-full text-sm transition-all ${
                        bikeDetails.hasIssues.includes(issue.id)
                          ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <span className="mr-1">{issue.icon}</span>
                      {issue.name}
                    </button>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full bg-cyan-600 hover:bg-cyan-700"
                onClick={handleGetRecommendations}
                disabled={!bikeDetails.condition || loading}
                data-testid="get-recommendations-btn"
              >
                {loading ? 'Analyzing...' : 'Get Recommendations'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && recommendations && (
            <div className="space-y-4">
              {/* Match Score */}
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-50 to-green-50 rounded-lg border border-cyan-200">
                <div className="bg-white p-2 rounded-full shadow-sm">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="font-medium">
                    {recommendations.match_score}% Match Found
                  </p>
                  <p className="text-sm text-gray-500">
                    Based on your {recommendations.bike_type?.name || 'bike'}
                  </p>
                </div>
                <button 
                  onClick={handleReset}
                  className="ml-auto text-sm text-cyan-600 hover:underline"
                >
                  Start over
                </button>
              </div>

              {/* Recommended Bundle */}
              {recommendations.recommended_bundle && (
                <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-white">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge className="bg-purple-600 text-white mb-2">
                          ⭐ Best Value
                        </Badge>
                        <h4 className="font-bold">
                          {recommendations.recommended_bundle.name}
                        </h4>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-400 line-through text-sm">
                          {currency}{recommendations.recommended_bundle.original_price}
                        </p>
                        <p className="font-bold text-lg text-purple-700">
                          {currency}{recommendations.recommended_bundle.final_price}
                        </p>
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          Save {recommendations.recommended_bundle.discount_percent}%
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {recommendations.recommended_bundle.included_services?.map((service, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {service.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                    <Button 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      onClick={() => {
                        onBundleSelect?.(recommendations.recommended_bundle);
                        setIsOpen(false);
                      }}
                      data-testid="select-bundle-btn"
                    >
                      Select Bundle
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Individual Services */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Bike className="h-4 w-4" />
                  Recommended Services
                </h4>
                <div className="space-y-2">
                  {recommendations.primary_services?.map((service, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          service.priority === 'critical' ? 'bg-red-500' :
                          service.priority === 'high' ? 'bg-amber-500' :
                          'bg-gray-300'
                        }`} />
                        <div>
                          <p className="font-medium text-sm">{service.name}</p>
                          {service.reason && (
                            <p className="text-xs text-gray-500">{service.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">
                          {currency}{service.price}
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            onServiceSelect?.(service);
                            setIsOpen(false);
                          }}
                          data-testid={`select-service-${service.type}`}
                        >
                          Select
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              {recommendations.tips?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    Tips for your {recommendations.bike_type?.name}
                  </h4>
                  <ul className="space-y-1">
                    {recommendations.tips.map((tip, idx) => (
                      <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-amber-500">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BikeCompatibilityChecker;
